import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import {createHomeHeader, getHomeHeader, updateHomeHeader} from "../../../api/axios";
import { Button } from "react-bootstrap";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from '../../../Utills/toast';
import FormGroup from "../../Common/FormGroup";

// Enhanced Validation Schema with file size and better validation
const validationSchema = Yup.object({
  header_paragraph_1: Yup.string()
    .trim()
    .required("Header paragraph 1 is required"),
  header_paragraph_2: Yup.string()
    .trim()
    .required("Header paragraph 2 is required"),
  header_paragraph_3: Yup.string()
    .trim()
    .required("Header paragraph 3 is required"),
  header_paragraph_4: Yup.string()
    .trim()
    .max(1000, "Header paragraph 4 must not exceed 1000 characters"),
  header_image: Yup.mixed()
    .test("required", "Header image is required", function(value) {
      const { old_header_image } = this.parent;
      // If there's a new file upload or an existing image, it's valid
      if (value instanceof File || old_header_image) {
        return true;
      }
      return false;
    })
    .test("fileType", "Only image files are allowed (JPEG, JPG, PNG, GIF, WEBP)", function(value) {
      // Only check file type if a new file is being uploaded
      if (!value || typeof value === 'string') return true;
      if (!(value instanceof File)) return true;
      const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return supportedFormats.includes(value.type);
    })
    .test('fileSize', 'Image size must be less than 5MB', function(value) {
      // Only check file size if a new file is being uploaded
      if (!value || typeof value === 'string') return true;
      if (!(value instanceof File)) return true;
      return value.size <= 5 * 1024 * 1024; // 5MB limit
    })
});

export default function HeaderEdit() {
  const { id, partnerId } = useParams();
  
  const [header, setHeader] = useState({
    header_paragraph_1: "",
    header_paragraph_2: "",
    header_paragraph_3: "",
    header_paragraph_4: "",
    header_image: "",
    old_header_image: "",
    index: null,
  });
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initial form values
  const initialValues = {
    header_paragraph_1: header.header_paragraph_1 || "PawprintID® was founded by a group of animal lovers who had already created a personal portable health record for humans - and wanted to protect the health of their pets too. Our founders, Beth and Jason, found it difficult to keep all of their pets important information organized and readily available, making it challenging to ensure their pets received the best care possible. This is where the idea for PawprintID® was born.",
    header_paragraph_2: header.header_paragraph_2 || "Jason and Beth worked tirelessly to apply the principles they developed for FootprintID® to a product that would make it easy for pet owners to access and update their pets medical information on the go.",
    header_paragraph_3: header.header_paragraph_3 || "Today",
    header_paragraph_4: header.header_paragraph_4 || "",
    header_image: header.header_image || "",
    old_header_image: header.header_image || "",
  };

  const getHomeHeaderById = async () => {
    try {
      const res = await getHomeHeader(id);
      const headerData = res.data.data[0];
      console.log(headerData);
      setHeader((prev)=>({
        ...prev,
        ...headerData,
        index: res.data.data.indexOf(headerData),
        old_header_image: headerData.header_image,
      }));
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Error loading data.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return
    };
    setIsLoading(true); 
    getHomeHeaderById(id);
  }, [id]);

  const handleSubmit = async (values, { setSubmitting, setFieldError, setFieldValue }) => {
    setSaving(true);

    var formData = new FormData();
    formData.append("header_paragraph_1", values.header_paragraph_1);
    formData.append("header_paragraph_2", values.header_paragraph_2);
    formData.append("header_paragraph_3", values.header_paragraph_3);
    formData.append("header_paragraph_4", values.header_paragraph_4);

    if (values.header_image instanceof File) {
      formData.append("header_image", values.header_image);
    }

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const res = await createHomeHeader(formData);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || "Header section has been created successfully!",
          });
          const headerData = res?.data?.data;
          setHeader(headerData);
          if (headerData && headerData.id) {
            navigate(`/${partnerId}/home/header/edit/${headerData.id}`);
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || "Failed to create header section. Please try again.!",
          });
        }
      } catch (err) {
        console.log(err);
        showToast({
          type: 'error',
          message: err?.response?.data?.message || "Failed to create header section. Please try again.!",
        });
      } finally {
        setSaving(false);
      }
    } else {
      try {
        const res = await updateHomeHeader(id, formData);
        
        if (res.status === 200) {
          console.log('success', res);
          showToast({
            type: 'success',
            message: res?.data?.message || "Header section has been updated successfully!",
          });
          
          // Update the form field with the returned image URL if available
          const updatedHeaderData = res?.data?.data;
          if (updatedHeaderData && updatedHeaderData.header_image) {
            setFieldValue("header_image", updatedHeaderData.header_image);
            setFieldValue("old_header_image", updatedHeaderData.header_image);
            setHeader(prev => ({
              ...prev,
              ...updatedHeaderData,
              old_header_image: updatedHeaderData.header_image
            }));
          }
          
          // Clear the file input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || "Failed to update header section. Please try again.!",
          });
        }
      } catch (err) {
        console.log(err);
        showToast({
          type: 'error',
          message: err?.response?.data?.message || "Failed to update header section. Please try again.!",
        });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
   <>
    {isLoading ? <CmsCircularProgress/> : 
      <FormWrapper>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            getFieldProps,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Title>{Number(id) ? 'Edit' : 'Add' } header section</Title>
              
              <FormGroup 
                label="Enter a new part 1" 
                requiredField
                error={touched.header_paragraph_1 && errors.header_paragraph_1 ? errors.header_paragraph_1 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  {...getFieldProps('header_paragraph_1')}
                  isInvalid={touched.header_paragraph_1 && !!errors.header_paragraph_1}
                />
              </FormGroup>

              <FormGroup 
                label="Enter a new part 2" 
                requiredField
                error={touched.header_paragraph_2 && errors.header_paragraph_2 ? errors.header_paragraph_2 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  {...getFieldProps('header_paragraph_2')}
                  isInvalid={touched.header_paragraph_2 && !!errors.header_paragraph_2}
                />
              </FormGroup>

              <FormGroup 
                label="Enter a new part 3" 
                requiredField
                error={touched.header_paragraph_3 && errors.header_paragraph_3 ? errors.header_paragraph_3 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  {...getFieldProps('header_paragraph_3')}
                  isInvalid={touched.header_paragraph_3 && !!errors.header_paragraph_3}
                />
              </FormGroup>

              <FormGroup 
                label="Enter a new part 4" 
                error={touched.header_paragraph_4 && errors.header_paragraph_4 ? errors.header_paragraph_4 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  {...getFieldProps('header_paragraph_4')}
                  isInvalid={touched.header_paragraph_4 && !!errors.header_paragraph_4}
                />
              </FormGroup>

              <FormGroup 
                label="Select a new Image" 
                requiredField
                error={touched.header_image && errors.header_image ? errors.header_image : ''}
              >       
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setFieldValue("header_image", file);
                  }}
                  onBlur={getFieldProps('header_image').onBlur}
                  style={{ display: "none" }}
                  ref={inputRef}
                  name="header_image"
                />
                
                {values.header_image instanceof File ? (
                  <ImageWrapper>
                    <UploadImage>  
                       <BootstrapImage
                         src={URL.createObjectURL(values.header_image)}
                        alt="header_image"
                        onClick={() => {
                          inputRef.current.click();
                        }}
                      />
                    </UploadImage>
                    <br />
                    <RemoveButtonWrapper>
                      <BootstrapButton
                        type="button"
                        onClick={() => {
                          setFieldValue("header_image", values.old_header_image || "");
                          if (inputRef.current) {
                            inputRef.current.value = '';
                          }
                        }}
                      >
                       <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>
                  </ImageWrapper>
                ) : (values.old_header_image || values.header_image) ? (
                  <UploadImage>
                    <BootstrapImage
                      src={values.old_header_image || values.header_image}
                      alt="header_image"
                      onClick={() => {
                        inputRef.current.click();
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = 0.8;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = 1;
                      }}
                    />
                  </UploadImage>
                ) : (
                  <UploadButton 
                    type="button" 
                    className="w-auto rounded-2 mb-0 mx-0 p-2" 
                    onClick={() => {
                      inputRef.current.click();
                    }}
                    hasError={touched.header_image && !!errors.header_image}
                  > 
                    Upload the image
                  </UploadButton>
                )}
              </FormGroup>
              
              <br/>
              <ButtonWrapper>
                <Link to={`/${partnerId}/home/header/view/${Number(id) ? id : 'create'}`}>
                  <Button variant="secondary">Go Back</Button>
                </Link>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving || isSubmitting}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormWrapper>
    }
   </>
  );
}

const FormWrapper = styled.div`
max-width: 865px;
margin:auto;
padding:10px;
.was-validated .form-control:valid:focus,
.form-control.is-valid,
.was-validated .form-control:valid,
.was-validated .form-control:valid {
  background-image: none !important;
 border-color: #86B7FE;
box-shadow: 0 0 0 .25rem rgba(13, 110, 253, .25) !important;
}
.was-validated .form-control:valid:focus,
.form-control.is-valid:focus,
.was-validated .form-control:valid:focus {
  background-image: none !important;
  box-shadow: 0 0 0 .25rem rgba(13, 110, 253, .25) !important;
}
.form-control.is-invalid,
.was-validated .form-control:invalid {
  box-shadow: 0 0 0 .25rem rgba(220,53,69,.25) !important;
}
.was-validated .form-control:valid:focus,
.form-control.is-invalid:focus,
.was-validated .form-control:invalid:focus {
  background-image: none !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
`;
const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;
const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
       @media (max-width:576px) {
          top: -9px;
        right: -17px;
  }
`;
const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
    @media (max-width: 890px) {
    gap: 2rem;
  }
 @media (max-width: 425px) {
  gap: 1rem;
}
`;
const UploadImage = styled.div`
  max-width: 15vw;
   overflow: hidden;
   border-radius: 6px;
   @media (max-width:576px) {
   max-width: 40vw;
  }
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));
const ImageWrapper = styled.div`
    position:relative;
    max-width: 15vw;
   @media (max-width:576px) {
   max-width: 40vw;
  }
`;
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #DC3545',
  "&:hover": {
    background: '#FFFEFF',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));

const UploadButton = styled.button`
  background-color: #007bff;
  color: white;
  border: 1px solid #007bff;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  
  &:hover {
    background-color: #0056b3;
    border-color: #0056b3;
  }
`;